import { AfterContentChecked, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import * as _ from 'underscore';
import { FormlyType } from '../ga-formly.service';
import { GaFormlyUtil } from '../ga-formly.util';
import { ExtraFieldWrapper } from '../extra-field-wrapper';

@Component({
  selector: 'formly-horizontal-wrapper',
  template: `
    <div class="formly-field wrapper-field">
      <div class="group" [class.is-invalid]="hasError" *ngIf="!to.noWrapper" [ngClass]="to.groupClass">
        <div class="row value-row">
          <label class="form-label col-form-label {{to.labelClass}}" [ngClass]="to.labelNgClass">
            <ng-container *ngIf="to.editable">
              <editable-text [key]="to.translateKey" [menu]="editableMenuOptions.join()" [initValues]="to.initValues" [screen]="to.screen" [category]="to.category" highlight=".label-text"></editable-text>
            </ng-container>

            <span class="label-text" [class.req]="to.required || childHasRequired">{{to.label}}</span>

            <!-- info icon -->
            <i class="fa fa-info-circle info-icon right" *ngIf="to.info && !to.noInfo" (click)="toggleInfo()"></i>
          </label>
          <ng-container *ngTemplateOutlet="control"></ng-container>
        </div>
        <div class="row info-row" *ngIf="to.showInfo && !to.noInfo">
          <div class="info" [ngClass]="infoClass" [innerHTML]="to.info"></div>
        </div>
      </div>
      
      <div [class.is-invalid]="hasError" *ngIf="to.noWrapper">
        <ng-container *ngTemplateOutlet="control"></ng-container>
      </div>
    </div>

    <ng-template #control>
      <div class="control" [ngClass]="to.valueClass" [class.is-invalid]="hasError">
        <div [ngClass]="{ 'input-group input-group-sm': (to.showAddOn && to.addOn) || to.addOnButton }">
          <ng-template #fieldComponent></ng-template>
          <span class="formly-activity-indicator" *ngIf="to.loading && field.type !== '${FormlyType.NG_SELECT}'"><i class="fa fa-spinner fa-spin activity-indicator"></i></span>
          <ng-container *ngIf="to.addOnButton || (to.showAddOn && to.addOn)">
            <span class="input-group-text" *ngIf="to.showAddOn && to.addOn" [innerHTML]="to.addOn"></span>
            <span class="input-group-btn" *ngIf="to.addOnButton">
              <ndc-dynamic [ndcDynamicComponent]="to.addOnButton.component" [ndcDynamicInputs]="to.addOnButton.inputs" [ndcDynamicOutputs]="to.addOnButton.outputs"></ndc-dynamic>
            </span>
          </ng-container>
        </div>

        <div *ngIf="hasError" class="validation validation-error" [class.no-wrapper-offset]="isNoWrapperCheckboxOrRadio">
          <span class="help-block" *ngIf="!to.validationErrorMessage"><formly-validation-message [field]="field"></formly-validation-message></span>
          <span class="help-block" [innerHTML]="to.validationErrorMessage" *ngIf="to.validationErrorMessage"></span>
        </div>

        <span class="formly-activity-indicator" *ngIf="to.loading && field.type !== '${FormlyType.NG_SELECT}' && !(to.showAddOn || to.addOnButton)"><i class="fa fa-spinner fa-spin activity-indicator"></i></span>
        <span [ngClass]="valueInfoClass">
          <div class="form-field-info-wrapper col-space-top" *ngIf="to.valueInfo">
            <!-- value popover info -->
            <ng-container *ngIf="to.valueInfo.label() && to.valueInfo.content(); else regularValueInfoField">
              <ng-template #formFieldInfoComponent>
                <ndc-dynamic [ndcDynamicComponent]="to.valueInfo.component" [ndcDynamicInputs]="to.valueInfo.inputs" [ndcDynamicOutputs]="to.valueInfo.outputs"></ndc-dynamic>
              </ng-template>
  
              <ng-template #formFieldInfoContent>
                <div class="form-field-info-content" [innerHtml]="to.valueInfo.content() | safe: 'html'"></div>
              </ng-template>
  
              <span #popover="bs-popover" [popover]="to.valueInfo.contentType === 'html' ? formFieldInfoContent : formFieldInfoComponent" triggers [isOpen]="isValueInfoOpen" placement="bottom" [outsideClick]="true" (onHidden)="isValueInfoOpen = false" container="body">
                {{to.valueInfo.label()}} <i class="fa fa-info-circle info-icon" (click)="isValueInfoOpen = !isValueInfoOpen"></i>
              </span>
            </ng-container>
  
            <ng-template #regularValueInfoField>
              <span [innerHTML]="to.valueInfo.content() | safe: 'html'" *ngIf="to.valueInfo.content()"></span>
            </ng-template>
          </div>
        </span>
      </div>

      <div class="hint" [class.no-wrapper-offset]="isNoWrapperCheckboxOrRadio" *ngIf="to.hint">
        <small class="mt-0 text-muted col-space-top col-space-bottom" [ngClass]="infoClass" [innerHTML]="to.hint"></small>
      </div>
    </ng-template>
  `,
  styleUrls: ['../fields/formly-field.scss'],
  encapsulation: ViewEncapsulation.None
})
export class FormlyHorizontalWrapperComponent extends ExtraFieldWrapper implements OnInit, OnDestroy, AfterContentChecked {
  public infoClass:string;
  public editableMenuOptions:string[] = [
    'label',
    'info'
  ];
  public childHasRequired:boolean;
  public isValueInfoOpen:boolean;
  public valueInfoClass:string;
  private defaultLabel:string;
  private defaultInfo:string;
  private defaultPlaceholder:string;
  private labelChangeHandler:Subscription;
  private infoChangeHandler:Subscription;
  private placeholderChangeHandler:Subscription;

  constructor(
    private translateService:TranslateService,
    private formlyUtil:GaFormlyUtil
  ) {
    super();
  }

  public ngOnInit():void {
    // set field classes (info, label, value)
    this.setFieldClasses();

    // init default options
    this.initDefault();

    this.to.label = this.getFieldLabel();
    this.to.info = this.getInfoText();
    this.to.placeholder = this.getPlaceholderText();
    this.valueInfoClass = this.formlyUtil.createClassNames(this.field.type, `${this.key}_INFO`, `${this.to.label} Info Label`);

    if (this.field.type === FormlyType.INPUT || this.field.type === FormlyType.SEARCH) {
      this.editableMenuOptions.push('placeholder');
    }

    // display the star on the group level when at least one of the child fields has required = true
    if (this.field.type === FormlyType.GROUP) {
      _.each(this.field.data?.fields, (field) => {
        if (field.templateOptions.required) {
          this.childHasRequired = true;
        }
      });
    }

    // register listener
    this.listen();
  }

  public ngAfterContentChecked():void {
    // reset default value to undefined rather than null
    this.formlyUtil.defaultValueToUndefined(this.formControl, this.field);
  }

  public toggleInfo():void {
    this.to.showInfo = !this.to.showInfo;
  }

  public get hasError():boolean {
    // noError is set by the custom option withoutError() to disable the error of a field (e.g.: order-entry-webshop.component)
    return !this.to.noError && (this.showError || !!this.to.validationErrorMessage);
  }

  public get isNoWrapperCheckboxOrRadio():boolean {
    return (this.field.type === FormlyType.CHECKBOX || this.field.type === FormlyType.RADIO) && this.to.noWrapper;
  }

  private listen():void {
    // listen on translation label changes
    this.labelChangeHandler = this.translateService.getStreamOnTranslationChange(this.to.translateKeyLabel)
      .subscribe(() => this.to.label = this.getFieldLabel());

    // listen on translation info changes
    this.infoChangeHandler = this.translateService.getStreamOnTranslationChange(this.to.translateKeyInfo)
      .subscribe(() => this.to.info = this.getInfoText());

    // listen on translation placeholder changes
    this.placeholderChangeHandler = this.translateService.getStreamOnTranslationChange(this.to.translateKeyPlaceholder)
      .subscribe(() => this.to.placeholder = this.getPlaceholderText());
  }

  private initDefault():void {
    // these default values reserved when translationKey label has been deleted
    // then will fall back to this default label instead
    this.defaultLabel = this.to.label;
    this.defaultInfo = this.to.info;
    this.defaultPlaceholder = this.to.placeholder;
  }

  private setFieldClasses():void {
    const defaultLabelClass:string = 'col-sm-3', defaultValueClass:string = 'col-sm-9';
    this.to.labelClass = this.to.labelClass || defaultLabelClass;
    if (!this.to.noWrapper) {
      this.to.valueClass = (this.to.valueClass || defaultValueClass);
    }

    const fieldFullWidth:boolean = this.to.labelClass.search(/col\-(\w+)\-12/) >= 0;
    let infoClasses:string[] = this.to.infoClass?.split(/\s+/) || [];

    if (this.to.noInfoOffset) {
      if (!this.to.infoClass) {
        infoClasses.push('col-md-12');
      }
    } else if (!fieldFullWidth) { // do not apply offset for fields having full width (col-*-12)
      //calculate offsets from label
      _.forEach(this.to.labelClass.split(/\s+/), (lc:string) => {
        infoClasses.push(
          lc.replace(/col-(\w+)-(\d+)/, 'offset-$1-$2')
        );
      });
    }

    // when full offset do not apply value class
    if(!this.to.noInfoOffset) {
      infoClasses = infoClasses.concat(this.to.valueClass && this.to.valueClass.split(/\s+/));
    }

    this.infoClass = this.to.noWrapper ? 'col-md-12' : infoClasses.join(' ');
  }

  private getFieldLabel():string {
    const screenTranslatedLabel:string = this.translateService.instant(this.to.translateKeyLabel);
    const isTranslated:boolean = screenTranslatedLabel !== this.to.translateKeyLabel;

    // if screen label is successfully translated then use screen translated label else fallback to normal label
    // if normal label is undefined then will display as empty
    // default label will be used if translation is deleted from editable-text component
    return isTranslated? screenTranslatedLabel : (
      this.to.label && this.to.label === this.defaultLabel? this.translateService.instant(this.to.label) :
        this.defaultLabel? this.translateService.instant(this.defaultLabel) : this.defaultLabel
    );
  }

  private getInfoText():string {
    const screenTranslatedInfo:string = this.translateService.instant(this.to.translateKeyInfo);
    const isTranslated:boolean = screenTranslatedInfo !== this.to.translateKeyInfo;

    // if screen info is successfully translated then use screen translated info else fallback to normal info
    // if normal info is undefined then will display as empty
    // default info will be used if translation is deleted from editable-text component
    return isTranslated? screenTranslatedInfo : (
      this.to.info && this.to.info === this.defaultInfo? this.translateService.instant(this.to.info) :
        this.defaultInfo? this.translateService.instant(this.defaultInfo) : this.defaultInfo
    );
  }

  private getPlaceholderText():string {
    const screenTranslatedPlaceholder:string = this.translateService.instant(this.to.translateKeyPlaceholder);
    const isTranslated:boolean = screenTranslatedPlaceholder !== this.to.translateKeyPlaceholder;
    const fieldLabel:string = this.getFieldLabel();
    const placeholderText:string = this.to.placeholder && this.to.placeholder === this.defaultPlaceholder? this.translateService.instant(this.to.placeholder) :
      this.defaultPlaceholder? this.translateService.instant(this.to.placeholder) : this.defaultPlaceholder;

    // if screen placeholder is successfully translated then use screen translation placeholder else fallback to normal placeholder
    // if normal placeholder is undefined then will display as empty
    // if normal placeholder is undefined but .withPlaceholder() used then will display label
    // default placeholder will be used if translation is deleted from editable-text component
    if (this.to.hidePlaceholder) {
      return '';
    }

    if (isTranslated) {
      return screenTranslatedPlaceholder;
    } else {
      if ((this.to.hasPlaceholder && (!this.to.placeholder || this.to.placeholder === fieldLabel) || this.to.noWrapper) && this.field.type !== FormlyType.SEARCH) {
        return placeholderText || fieldLabel;
      } else {
        return placeholderText;
      }
    }
  }

  public ngOnDestroy():void {
    // unsubscribe listeners
    this.labelChangeHandler?.unsubscribe();
    this.infoChangeHandler?.unsubscribe();
    this.placeholderChangeHandler?.unsubscribe();
  }
}